import React, { useState } from "react";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import { Formik } from "formik";
import { useErrorMessage } from "@poseidon/ui/app/toasts/useErrorMessage";
import { useToastMessage } from "@poseidon/ui/app/toasts/useToastMessage";
import LinearProgress from "@mui/material/LinearProgress";
import { useRequote } from "./useRequote";
import { useRequotePreview } from "./useRequotePreview";
import { RequotePreview } from "./RequotePreview";
import { RequoteForm } from "./RequoteForm";
import { Panel } from "@poseidon/ui/components/Panel";
import {
  FormikDisabler,
  usePermissions,
} from "@poseidon/ui/components";

export const Requote = ({ application }) => {
  const { canWrite } = usePermissions();
  const { setError } = useErrorMessage();
  const { setToast } = useToastMessage();
  const { requote, success } = useRequote(application.id);
  const [saving, setSaving] = useState(false);
  const {
    requotePreview,
    preview,
    loading,
    error: previewError,
  } = useRequotePreview(application.id, success);
  const onSubmitHandler = async (values) => {
    try {
      requotePreview(values).then((success) => {
        if (!success) {
          setError("Error previewing quote");
        }
      });
    } catch (error) {
      setError(error.message);
    }
  };
  const applyRequote = async (values) => {
    try {
      setSaving(true);
      requote(values).then((success) => {
        if (success) {
          setToast("Applied Quote");
        } else {
          setError("Error applying quote");
        }
        setSaving(false);
      });
    } catch (error) {
      setError(error.message);
      setSaving(false);
    }
  };
  const initialValues = {
    mode: "term",
    deposit: application.deposit,
    costOfVessel: application.costOfVessel,
    baseRate: application.baseRate,
    addonRate: application.addonRate,
    term: application.termMonths,
    repayment: application.monthlyPayment,
    // echo any existing balloon so requotes keep it unless changed; blank for
    // standard applications so the payload stays exactly as before
    balloon: application.balloon > 0 ? application.balloon : "",
    lookupRate: false,
    ignoreLimits: false,
  };
  // Mirror the backend rule 0 <= balloon < principal so the error shows
  // inline on the balloon input rather than only after submitting.
  const validate = (values) => {
    const errors = {};
    if (values.balloon !== "" && values.balloon != null) {
      const balloon = Number(values.balloon);
      const principal = Number(values.costOfVessel) - Number(values.deposit);
      if (Number.isNaN(balloon)) {
        errors.balloon = "Enter a number";
      } else if (balloon < 0) {
        errors.balloon = "Balloon must not be negative";
      } else if (Number.isFinite(principal) && balloon >= principal) {
        errors.balloon = "Balloon must be less than the loan principal";
      }
    }
    return errors;
  };
  return (
    <Formik
      initialValues={initialValues}
      validate={validate}
      onSubmit={onSubmitHandler}
      enableReinitialize
    >
      <>
        <FormikDisabler disabled={!canWrite} />
        <Panel title="Requote" expandable startCollapsed={true}>
          <Grid container spacing={5}>
            <Grid item xs={12} md={6}>
              <RequoteForm />
            </Grid>
            <Grid item xs={12} md={6}>
              {loading && <LinearProgress />}
              {!!previewError && (
                <Alert severity="error">{previewError?.message}</Alert>
              )}
              {preview && !previewError && (
                <RequotePreview
                  data={preview}
                  onApply={applyRequote}
                  saving={saving}
                  loading={loading}
                />
              )}
            </Grid>
          </Grid>
        </Panel>
      </>
    </Formik>
  );
};
